import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "datePresets", "dateFrom", "dateTo",
    "exportBtn", "exportSpinner"
  ]

  static values = {
    exportUrl: String
  }

  connect() {
    this._currentPreset = "this_month"
    this._dateStart = null
    this._dateEnd = null
  }

  // ── Modal ──────────────────────────────────────────────────────────

  open(event) {
    event.preventDefault()
    document.dispatchEvent(new CustomEvent("dropdown:close"))
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._applyPresetDates("this_month")
    this._updateDatePresetChips()
  }

  hideModal(event) {
    if (event.target === this.modalTarget) {
      this._close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  close() {
    this._close()
  }

  _close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ── Date Presets ───────────────────────────────────────────────────

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentPreset = preset
    this._applyPresetDates(preset)
    this._updateDatePresetChips()
  }

  filterDatesCustom() {
    this._currentPreset = ""
    this._updateDatePresetChips()
    this._dateStart = this.dateFromTarget.value
    this._dateEnd = this.dateToTarget.value
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = null
    let to = null

    if (preset === "this_month") {
      from = new Date(today.getFullYear(), today.getMonth(), 1)
      to = new Date(today)
    } else if (preset === "last_month") {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      to = new Date(today.getFullYear(), today.getMonth(), 0)
    } else if (preset === "last_3_months") {
      from = new Date(today.getFullYear(), today.getMonth() - 3, 1)
      to = new Date(today)
    } else if (preset === "last_6_months") {
      from = new Date(today.getFullYear(), today.getMonth() - 6, 1)
      to = new Date(today)
    }

    if (from && to) {
      this._dateStart = this._formatDate(from)
      this._dateEnd = this._formatDate(to)

      if (this.hasDateFromTarget) this.dateFromTarget.value = this._dateStart
      if (this.hasDateToTarget) this.dateToTarget.value = this._dateEnd
    }
  }

  _updateDatePresetChips() {
    if (!this.hasDatePresetsTarget) return
    const buttons = this.datePresetsTarget.querySelectorAll("button")
    const activeClasses = ["bg-blue-50", "text-blue-600", "font-medium", "border-blue-200"]
    const inactiveClasses = ["text-gray-500", "hover:bg-gray-50", "border-gray-200"]

    buttons.forEach((btn) => {
      const isActive = btn.dataset.preset === this._currentPreset
      btn.classList.remove(...activeClasses, ...inactiveClasses)
      if (isActive) {
        btn.classList.add(...activeClasses)
      } else {
        btn.classList.add(...inactiveClasses)
      }
    })
  }

  _formatDate(d) {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  // ── Export ──────────────────────────────────────────────────────────

  async exportXlsx() {
    if (!this._dateStart || !this._dateEnd) {
      this._showToast("Selecione o período.", "error")
      return
    }

    this.exportBtnTarget.disabled = true
    this.exportSpinnerTarget.classList.remove("hidden")

    const params = new URLSearchParams()
    params.append("start_date", this._dateStart)
    params.append("end_date", this._dateEnd)

    try {
      const url = `${this.exportUrlValue}?${params.toString()}`
      const resp = await fetch(url, {
        headers: { "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
      })

      if (!resp.ok) {
        throw new Error("Erro ao gerar relatório.")
      }

      const blob = await resp.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.style.display = "none"
      a.href = blobUrl
      a.download = "headcount.xlsx"
      document.body.appendChild(a)
      a.click()
      URL.revokeObjectURL(blobUrl)
      a.remove()

      this._close()
      this._showToast("Headcount exportado com sucesso.")
    } catch (e) {
      console.error("Headcount export error:", e)
      this._showToast(e.message || "Erro ao gerar relatório.", "error")
    } finally {
      this.exportBtnTarget.disabled = false
      this.exportSpinnerTarget.classList.add("hidden")
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
